/**
 * Formulario para crear/editar tareas de mantenimiento
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePlotters,
  useOperators,
  useCreateMaintenance,
  useUpdateMaintenance,
} from '@/hooks/usePlotterMaintenance';
import {
  plotterMaintenanceFormSchema,
  type PlotterMaintenanceFormData,
} from '@/schemas/plotterMaintenanceSchemas';
import type { PlotterMaintenance } from '@/types/plotterMaintenance';
import {
  TASK_TYPE_CONFIG,
  RECURRENCE_CONFIG,
} from '@/types/plotterMaintenance';

interface MaintenanceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingTask?: PlotterMaintenance | null;
  defaultMachineId?: string;
}

export function MaintenanceFormDialog({
  open,
  onOpenChange,
  editingTask,
  defaultMachineId,
}: MaintenanceFormDialogProps) {
  const { user } = useAuth();
  const { data: plotters = [], isLoading: loadingPlotters } = usePlotters();
  const { data: operators = [], isLoading: loadingOperators } = useOperators();
  const createMutation = useCreateMaintenance();
  const updateMutation = useUpdateMaintenance();

  const isEditing = !!editingTask;
  const isLoading = createMutation.isPending || updateMutation.isPending;

  const form = useForm<PlotterMaintenanceFormData>({
    resolver: zodResolver(plotterMaintenanceFormSchema),
    defaultValues: {
      machine_id: defaultMachineId || '',
      task_type: 'routine_cleaning',
      title: '',
      description: '',
      scheduled_date: new Date().toISOString().slice(0, 16),
      estimated_duration_minutes: 30,
      is_recurring: false,
      recurrence_pattern: null,
      recurrence_interval: 1,
      assigned_to: '',
      priority: 5,
      notes: '',
      checklist: [],
    },
  });

  const watchTaskType = form.watch('task_type');
  const watchIsRecurring = form.watch('is_recurring');
  const checklist = form.watch('checklist');

  // Reset form cuando cambia la tarea
  useEffect(() => {
    if (editingTask) {
      form.reset({
        machine_id: editingTask.machine_id,
        task_type: editingTask.task_type,
        title: editingTask.title,
        description: editingTask.description || '',
        scheduled_date: editingTask.scheduled_date.slice(0, 16),
        estimated_duration_minutes: editingTask.estimated_duration_minutes,
        is_recurring: editingTask.is_recurring,
        recurrence_pattern: editingTask.recurrence_pattern || null,
        recurrence_interval: editingTask.recurrence_interval,
        assigned_to: editingTask.assigned_to || '',
        priority: editingTask.priority,
        notes: editingTask.notes || '',
        checklist: editingTask.checklist || [],
      });
    } else {
      form.reset({
        machine_id: defaultMachineId || '',
        task_type: 'routine_cleaning',
        title: '',
        description: '',
        scheduled_date: new Date().toISOString().slice(0, 16),
        estimated_duration_minutes: 30,
        is_recurring: false,
        recurrence_pattern: null,
        recurrence_interval: 1,
        assigned_to: '',
        priority: 5,
        notes: '',
        checklist: [],
      });
    }
  }, [editingTask, defaultMachineId, form]);

  // Auto-rellenar título y checklist cuando cambia el tipo
  useEffect(() => {
    if (!isEditing && watchTaskType) {
      const config = TASK_TYPE_CONFIG[watchTaskType];
      form.setValue('title', config.label);
      form.setValue('estimated_duration_minutes', config.defaultDuration);
      form.setValue('checklist', config.defaultChecklist.map(task => ({
        task,
        completed: false,
      })));
    }
  }, [watchTaskType, isEditing, form]);

  const onSubmit = async (data: PlotterMaintenanceFormData) => {
    try {
      if (isEditing && editingTask) {
        await updateMutation.mutateAsync({
          id: editingTask.id,
          data,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          created_by: user?.id,
        });
      }
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const addChecklistItem = () => {
    const current = form.getValues('checklist');
    form.setValue('checklist', [...current, { task: '', completed: false }]);
  };

  const removeChecklistItem = (index: number) => {
    const current = form.getValues('checklist');
    form.setValue('checklist', current.filter((_, i) => i !== index));
  };

  const updateChecklistItem = (index: number, value: string) => {
    const current = form.getValues('checklist');
    const updated = [...current];
    updated[index] = { ...updated[index], task: value };
    form.setValue('checklist', updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Tarea de Mantenimiento' : 'Nueva Tarea de Mantenimiento'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Plotter */}
              <FormField
                control={form.control}
                name="machine_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plotter *</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={loadingPlotters}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar plotter" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {plotters.map((plotter) => (
                          <SelectItem key={plotter.id} value={plotter.id}>
                            {plotter.name} {plotter.code && `(${plotter.code})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tipo de tarea */}
              <FormField
                control={form.control}
                name="task_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Tarea *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(TASK_TYPE_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            {config.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Título */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre de la tarea" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Descripción */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detalles adicionales..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fecha programada */}
              <FormField
                control={form.control}
                name="scheduled_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha y Hora *</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Duración estimada */}
              <FormField
                control={form.control}
                name="estimated_duration_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duración Estimada (min)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={480}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Recurrencia */}
            <div className="space-y-4 p-4 border rounded-lg">
              <FormField
                control={form.control}
                name="is_recurring"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <FormLabel>Tarea Recurrente</FormLabel>
                      <FormDescription>
                        Programar automáticamente la siguiente tarea al completar
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {watchIsRecurring && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <FormField
                    control={form.control}
                    name="recurrence_pattern"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Patrón</FormLabel>
                        <Select
                          value={field.value || ''}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.entries(RECURRENCE_CONFIG).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                {config.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recurrence_interval"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cada X períodos</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={365}
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Asignar a */}
              <FormField
                control={form.control}
                name="assigned_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asignar a</FormLabel>
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                      disabled={loadingOperators}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sin asignar" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Sin asignar</SelectItem>
                        {operators.map((op) => (
                          <SelectItem key={op.id} value={op.id}>
                            {op.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Prioridad */}
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridad: {field.value}</FormLabel>
                    <FormControl>
                      <Slider
                        min={1}
                        max={10}
                        step={1}
                        value={[field.value]}
                        onValueChange={(v) => field.onChange(v[0])}
                      />
                    </FormControl>
                    <FormDescription className="flex justify-between text-xs">
                      <span>Baja</span>
                      <span>Alta</span>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <FormLabel>Checklist</FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addChecklistItem}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
              <div className="space-y-2">
                {checklist.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={item.task}
                      onChange={(e) => updateChecklistItem(index, e.target.value)}
                      placeholder="Tarea del checklist"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChecklistItem(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Notas */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notas adicionales..."
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Guardar Cambios' : 'Programar Tarea'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
